import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Input, Button, Tabs, Card, Typography, Tag, Space, Spin, message, Tour, Alert, Upload } from 'antd';
import { 
  BookOutlined, 
  PlusOutlined, 
  RocketOutlined, 
  FolderOutlined, 
  SearchOutlined, 
  BulbOutlined, 
  DeleteOutlined, 
  InfoCircleOutlined,
  ExperimentOutlined,
  CodeOutlined,
  RobotOutlined,
  FormOutlined,
  MailOutlined,
  ShoppingOutlined,
  TeamOutlined,
  DatabaseOutlined,
  ApiOutlined,
  FileTextOutlined,
  UserOutlined,
  ImportOutlined,
  SendOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  ThunderboltFilled
} from '@ant-design/icons';
import AIWorkflowGeneratorModal from './AIWorkflowGeneratorModal';
import { defaultTemplates } from './defaultTemplates';
import { adminInstance } from './api/axiosConfig';
import { useReactFlow } from '@xyflow/react';
import WorkflowGenerationLoader from './WorkflowGenerationLoader';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Dragger } = Upload;

// Define category icons mapping
const categoryIcons = {
  'My Templates': <UserOutlined />,
  'Content Generation': <FileTextOutlined />,
  'Customer Service': <TeamOutlined />,
  'Data Processing': <DatabaseOutlined />,
  'E-commerce': <ShoppingOutlined />,
  'Email Marketing': <MailOutlined />,
  'Form Processing': <FormOutlined />,
  'Integration': <ApiOutlined />,
  'Code Generation': <CodeOutlined />,
  'Bot Creation': <RobotOutlined />
};

// Function to get icon for a category
const getCategoryIcon = (category) => {
  return categoryIcons[category] || <FolderOutlined />;
};

// Workflow suggestions
const workflowSuggestions = [
  "Create a blog post generator that takes a topic and generates an outline, introduction, and full article",
  "Build a customer support AI agent that answers common questions based on my knowledge base",
  "Make a product description generator that turns technical specs into engaging marketing copy",
  "Create a social media campaign workflow that generates posts for multiple platforms"
];

// Style objects
const styles = {
  mainActionButton: {
    background: 'linear-gradient(90deg, #4285f4, #7b61ff)',
    border: 'none',
    height: '50px',
    fontSize: '16px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  suggestionCard: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderLeft: '3px solid #4285f4',
  },
  templateCard: {
    backgroundColor: '#374151',
    borderColor: '#4b5563',
    transition: 'all 0.2s ease',
    height: '100%',
  },
  categoryButton: (isSelected) => ({
    borderRadius: '16px',
    marginRight: '8px',
    marginBottom: '8px',
    ...(isSelected ? {
      background: 'linear-gradient(90deg, #4285f4, #7b61ff)',
      color: 'white',
      border: 'none',
    } : {
      backgroundColor: '#374151',
      borderColor: '#4b5563',
      color: '#e5e7eb',
    })
  }),
};

const TemplateModal = ({ 
  visible, 
  onCancel, 
  onSelectTemplate, 
  onCreateNew,
  workflowInstance,
  onImport,
  onGenerate  // Make sure this prop is used
}) => {
  const { setNodes, setEdges } = useReactFlow();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    difficulty: 'all',
    search: ''
  });
  const [isTutorialMode, setIsTutorialMode] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [allTemplates, setAllTemplates] = useState({});
  const [isAIModalVisible, setIsAIModalVisible] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isTemplateVisible, setIsTemplateVisible] = useState(visible);
  const [prompt, setPrompt] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('create');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isGenerating, setIsGenerating] = useState(false);
  const [modalStack, setModalStack] = useState([]);
  const [hasOpenRouterKey, setHasOpenRouterKey] = useState(false);
  const [isCheckingAPI, setIsCheckingAPI] = useState(true);

  // Effect to track window size
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Effect to sync visible prop
  useEffect(() => {
    setIsTemplateVisible(visible);
  }, [visible]);

  
  useEffect(() => {
    if (visible) {
      checkOpenRouterAPI();
    }
  }, [visible]);

  // Calculate responsive modal width based on window size
  const getModalWidth = () => {
    // Special handling for the WordPress dashboard range (960-1330px)
    if (windowWidth >= 960 && windowWidth <= 1330) {
      // In this range, WordPress sidebar takes significant space
      return Math.min(windowWidth * 0.7, 850); // 70% of viewport, max 850px
    }
    
    // For other screen sizes, use the regular calculation
    return Math.min(Math.max(windowWidth * 0.9, 320), 1000);
  };

  // Determine if we're in mobile view
  const isMobileView = windowWidth < 768;

  // Fetch user templates
  useEffect(() => {
    if (visible) {
      fetchUserTemplates();
    }
  }, [visible]);

  const fetchUserTemplates = async () => {
    setLoading(true);
    try {
      const response = await adminInstance.get('/templates');
      // Combine user templates with default templates
      const userTemplateObjects = response.data.reduce((acc, template) => ({
        ...acc,
        [`user-${template.id}`]: {
          ...template,
          isUserTemplate: true,
        }
      }), {});
      
      setAllTemplates({
        ...defaultTemplates,
        ...userTemplateObjects
      });
    } catch (error) {
      console.error('Failed to fetch user templates:', error);
      message.error('Failed to load saved templates');
      setAllTemplates(defaultTemplates);
    } finally {
      setLoading(false);
    }
  };

  const checkOpenRouterAPI = async () => {
    setIsCheckingAPI(true);
    try {
      const response = await adminInstance.get('/settings');
      setHasOpenRouterKey(!!response.data.openrouter_api_key);
    } catch (error) {
      console.error('Error checking OpenRouter API:', error);
      setHasOpenRouterKey(false);
    } finally {
      setIsCheckingAPI(false);
    }
  };

  // Get unique categories and difficulties from default templates
  const categories = useMemo(() => {
    const defaultCats = [...new Set(Object.values(defaultTemplates)
      .map(t => t.category)
      .filter(Boolean))];
    return ['My Templates', ...defaultCats];
  }, []);

  const difficulties = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return Object.entries(allTemplates).filter(([id, template]) => {
      const searchTerm = filters.search.toLowerCase();
      const matchesCategory = 
        filters.category === 'all' || 
        (filters.category === 'My Templates' && template.isUserTemplate) ||
        (!template.isUserTemplate && template.category === filters.category);
      const matchesDifficulty = 
        filters.difficulty === 'all' || 
        (!template.isUserTemplate && template.difficulty === filters.difficulty);
      const matchesSearch = filters.search === '' || 
        template.name.toLowerCase().includes(searchTerm) ||
        (template.description || '').toLowerCase().includes(searchTerm) ||
        (template.tags || []).some(tag => tag.toLowerCase().includes(searchTerm));

      return matchesCategory && matchesDifficulty && matchesSearch;
    });
  }, [filters, allTemplates]);

  // Handle AI modal opening
  const showAIModal = () => {
    // Store current prompt when opening AI modal
    setPrompt(prompt);
    setModalStack([...modalStack, 'template']);
    setIsAIModalVisible(true);
    setIsTemplateVisible(false);
  };

  // Handle direct generate workflow from Create tab
  const handleGenerateWorkflow = async (inputPrompt) => {
    if (!hasOpenRouterKey) {
      message.error('Please configure OpenRouter API key first');
      return;
    }
    
    if (!inputPrompt.trim()) {
      message.error('Please enter a workflow description');
      return;
    }
  
    try {
      setIsGenerating(true);
      
      if (onGenerate) {
        await onGenerate(inputPrompt);
      } else {
        message.error('Workflow generation is not configured');
      }
    } catch (error) {
      console.error('Error generating workflow:', error);
      message.error('Failed to generate workflow: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const goToSettings = () => {
    window.location.href = `${window.wpAiWorkflowsSettings.adminUrl}admin.php?page=wp-ai-workflows&action=settings`;
  };

  // Handle import file upload
  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.json',
    showUploadList: false,
    beforeUpload: (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = JSON.parse(e.target.result);
          if (!content.nodes || !content.edges) {
            throw new Error('Invalid workflow file format');
          }
          onImport(content);
          onCancel(); // Close modal after successful import
          message.success('Workflow imported successfully');
        } catch (error) {
          message.error('Error importing workflow: ' + error.message);
        }
      };
      reader.readAsText(file);
      return false;
    }
  };

  // Tutorial steps
  const tutorialSteps = useMemo(() => [
    {
      title: 'Welcome to Workflow Builder',
      target: () => document.querySelector('.reactflow-wrapper'),
      placement: 'right-start',
      description: (
        <Space direction="vertical">
          <Text>This is your workflow canvas. Notice the Trigger node already placed for you - this is where every workflow begins!</Text>
        </Space>
      )
    },
    // ... the rest of the tutorial steps would go here
  ], []);

  const startTutorial = async () => {
    onCreateNew(); // Create new workflow with trigger node
    setIsTutorialMode(true);
    onCancel(); // Close the template modal
  };

  // Delete user template
  const handleDeleteTemplate = async (templateId) => {
    try {
      const actualId = templateId.replace('user-', '');
      await adminInstance.delete(`/templates/${actualId}`);
      message.success('Template deleted successfully');
      fetchUserTemplates();
    } catch (error) {
      console.error('Delete template error:', error);
      message.error('Failed to delete template');
    }
  };

  // Handle suggestions
  const handleSuggestionClick = (suggestion) => {
    setPrompt(suggestion);
    setShowSuggestions(false); // Hide suggestions after selection
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Beginner': 'green',
      'Intermediate': 'blue',
      'Advanced': 'purple',
      'Expert': 'red'
    };
    return colors[difficulty] || 'blue';
  };

  return (
    <>
      {isGenerating && <WorkflowGenerationLoader />}
      <Modal
        open={isTemplateVisible && !isGenerating} 
        onCancel={onCancel}
        footer={null}
        width={getModalWidth()}
        style={{ maxWidth: '100vw' }}
        bodyStyle={{ 
          padding: 0,
          overflow: 'hidden',
          borderRadius: '8px',
          height: 'auto',
          maxHeight: '80vh'
        }}
        maskClosable={true}
        destroyOnClose={true}
        title={null}
        className="workflow-template-modal"
      >
        <Tabs 
          activeKey={activeTabKey} 
          onChange={setActiveTabKey}
          type="card"
          size={isMobileView ? "small" : "middle"}
          className="workflow-tabs"
          style={{
            height: '100%',
            backgroundColor: 'transparent'
          }}
        >
          <TabPane 
            tab={<span><ThunderboltFilled /> Create</span>}
            key="create"
          >
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button 
                  icon={<BulbOutlined />} 
                  onClick={() => setShowSuggestions(!showSuggestions)}
                >
                  {showSuggestions ? 'Hide Suggestions' : 'Show Suggestions'}
                </Button>
                <Button 
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onCreateNew}
                >
                  Start from Scratch
                </Button>
              </div>

              
              {!hasOpenRouterKey && !isCheckingAPI && (
                  <Alert
                    message="OpenRouter API Required"
                    description={
                      <Space direction="vertical" size="small">
                        <span>
                          To use the AI Workflow Generator, you need to configure your OpenRouter API key.
                          OpenRouter provides access to various AI models including Claude and GPT-4.
                        </span>
                        <Button 
                          type="primary" 
                          icon={<SettingOutlined />}
                          onClick={goToSettings}
                        >
                          Configure API Key
                        </Button>
                        <a 
                          href="https://openrouter.ai/keys" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          Get an OpenRouter API key
                        </a>
                      </Space>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
              
              {showSuggestions && (
                <div style={{ marginBottom: '20px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Workflow Suggestions:
                  </Text>
                  {workflowSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={styles.suggestionCard}
                      className="suggestion-card"
                    >
                      <Text>{suggestion}</Text>
                    </div>
                  ))}
                </div>
              )}
              
              <div style={{ marginBottom: '20px' }}>
              <TextArea
                placeholder={hasOpenRouterKey ? 
                  "Describe your AI workflow in detail, and we'll build it for you..." : 
                  "Please configure OpenRouter API key first"}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                autoSize={{ minRows: 4, maxRows: 8 }}
                style={{ 
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '16px',
                  marginBottom: '16px',
                  borderColor: '#d9d9d9'
                }}
                disabled={!hasOpenRouterKey}
              />

              <Button
                onClick={() => handleGenerateWorkflow(prompt)}
                icon={<ThunderboltFilled />}
                disabled={!prompt.trim() || !hasOpenRouterKey}
                block
                style={styles.mainActionButton}
                className="generate-workflow-button"
              >
                <span style={{ position: 'relative', zIndex: 1 }}>Generate Workflow with AI</span>
              </Button>

                
                
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Text type="secondary">
                    Our AI agent will create an almost complete functional workflow based on your description. You still need to check and fine-tune it.
                  </Text>
                </div>
              </div>
            </div>
          </TabPane>
          
          <TabPane 
          tab={<span><FolderOutlined /> Templates</span>}
          key="templates"
          >
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#ffffff',
            height: 'calc(80vh - 120px)',
            maxHeight: '600px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <Input
                placeholder="Search templates..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                style={{ 
                  flex: 1, 
                  minWidth: isMobileView ? '100%' : '200px',
                  backgroundColor: '#fafafa',
                  borderColor: '#d9d9d9'
                }}
              />
              
              <Space wrap>
                <Button
                  onClick={() => setFilters({ ...filters, category: 'all' })}
                  icon={<FolderOutlined />}
                  className={filters.category === 'all' ? 'category-button selected-category' : 'category-button'}
                  style={filters.category !== 'all' ? {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#d9d9d9',
                    color: '#666'
                  } : {}}
                >
                  All
                </Button>
                
                {categories.map(category => (
                  <Button
                    key={category}
                    onClick={() => setFilters({ ...filters, category: category })}
                    icon={getCategoryIcon(category)}
                    className={filters.category === category ? 'category-button selected-category' : 'category-button'}
                    style={filters.category !== category ? {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d9d9d9',
                      color: '#666'
                    } : {}}
                  >
                    {category}
                  </Button>
                ))}
              </Space>
            </div>
            
            <div style={{ 
              flex: 1,
              overflowY: 'auto',
              paddingRight: '4px',
              marginRight: '-4px'
            }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                </div>
              ) : filteredTemplates.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobileView 
                    ? '1fr' 
                    : windowWidth < 992 
                      ? 'repeat(auto-fill, minmax(250px, 1fr))'
                      : 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '16px',
                  paddingBottom: '20px',
                  paddingRight: '10px'
                }}>
                  {filteredTemplates.map(([id, template]) => (
                    <Card
                      key={id}
                      hoverable
                      className="template-card"
                      style={{
                        backgroundColor: '#ffffff',
                        borderColor: '#e8e8e8',
                        transition: 'all 0.2s ease',
                        height: '100%',
                      }}
                      actions={[
                        <Button 
                          type="primary" 
                          onClick={() => {
                            onSelectTemplate(id, !template.isUserTemplate);
                            onCancel();
                          }}
                        >
                          Use Template
                        </Button>,
                        template.isUserTemplate && (
                          <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />} 
                            onClick={() => handleDeleteTemplate(id)}
                          >
                            Delete
                          </Button>
                        )
                      ].filter(Boolean)}
                    >
                      <div>
                        {template.isUserTemplate ? (
                          <Tag color="#9254de">My Template</Tag>
                        ) : (
                          <>
                            <Tag color="#2f54eb">{template.category}</Tag>
                            {template.difficulty && (
                              <Tag color={getDifficultyColor(template.difficulty)}>
                                {template.difficulty}
                              </Tag>
                            )}
                          </>
                        )}
                        <Title level={5} style={{ marginTop: '8px', color: '#333' }}>{template.name}</Title>
                        <Text style={{ color: '#666' }}>{template.description}</Text>
                        {template.tags?.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            {template.tags.map(tag => (
                              <Tag key={tag}>{tag}</Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <InfoCircleOutlined style={{ fontSize: 36, color: '#999', marginBottom: 16 }} />
                  <Text style={{ display: 'block', color: '#666' }}>
                    No templates found for your selection. Try changing your filters.
                  </Text>
                </div>
              )}
            </div>
          </div>
          </TabPane>
          
          <TabPane 
            tab={<span><ImportOutlined /> Import</span>}
            key="import"
          >
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              backgroundColor: '#ffffff',
              minHeight: '450px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Dragger {...uploadProps} style={{ 
                padding: '40px 20px',
                backgroundColor: '#fafafa',
                borderColor: '#d9d9d9',
              }}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ fontSize: '32px', color: '#4285f4' }} />
                </p>
                <p className="ant-upload-text" style={{ fontSize: '16px', marginTop: '16px', color: '#333' }}>
                  Click or drag workflow file to this area
                </p>
                <p className="ant-upload-hint" style={{ color: '#666' }}>
                  Support for .json workflow files exported from AI Workflow Automation
                </p>
                <Button 
                  type="primary" 
                  style={{ marginTop: '24px', background: 'linear-gradient(90deg, #4285f4, #7b61ff)' }}
                  icon={<ImportOutlined />}
                >
                  Select File
                </Button>
              </Dragger>
              <div style={{ marginTop: '24px' }}>
                <Alert
                  message="Import will replace your current workspace"
                  description="Make sure to save any unsaved changes before importing a workflow."
                  type="info"
                  showIcon
                />
              </div>
            </div>
          </TabPane>
        </Tabs>
        
        <style jsx="true">{`
          /* Light theme modal styles */
          .workflow-template-modal .ant-modal-content {
            background-color: #ffffff;
            border-radius: 12px;
          }
          
          .workflow-template-modal .ant-modal-close-x {
            color: #333;
          }
          
          /* Tabs styling */
          .workflow-tabs > .ant-tabs-nav {
            margin-bottom: 0;
            background-color: transparent;
          }
          
          .workflow-tabs .ant-tabs-tab {
            padding: 12px 24px;
            background-color: #f5f5f5;
            border-color: #e8e8e8;
            color: #666;
            margin: 0;
          }
          
          .workflow-tabs .ant-tabs-tab:hover {
            color: #333;
          }
          
          .workflow-tabs .ant-tabs-tab-active {
            background-color: #ffffff;
            border-bottom-color: #ffffff;
            color: #4285f4;
          }
          
          .workflow-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #4285f4 !important;
          }
          
          .workflow-tabs .ant-tabs-ink-bar {
            background: linear-gradient(90deg, #4285f4, #7b61ff);
            height: 3px;
          }
          
          .workflow-tabs .ant-tabs-content {
            background-color: #ffffff;
          }
          
          .workflow-tabs .ant-tabs-content-holder {
            background-color: #ffffff;
          }
          
          /* Remove gray background from tabs nav */
          .workflow-tabs .ant-tabs-nav-wrap {
            background-color: transparent;
          }
          
          .workflow-tabs .ant-tabs-nav::before {
            border-bottom: none;
          }
          
          /* Tabs tabpane height management */
          .workflow-tabs .ant-tabs-tabpane {
            height: 100%;
          }
          
          /* Generate button styling */
        .generate-workflow-button {
            background: linear-gradient(90deg, #4285f4, #7b61ff) !important;
            border: none !important;
            height: 50px !important;
            font-size: 16px !important;
            font-weight: 600 !important;
            position: relative !important;
            overflow: hidden !important;
            color: white !important;
          }
          
          .generate-workflow-button > span {
            color: white !important;
          }
          
          .generate-workflow-button .anticon {
            color: white !important;
          }
          
          .generate-workflow-button:hover {
            background: linear-gradient(90deg, #3b78e7, #6a4ff2) !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(66, 133, 244, 0.25);
            color: white !important;
          }
          
          .generate-workflow-button:hover > span {
            color: white !important;
          }
          
          .generate-workflow-button:hover .anticon {
            color: white !important;
          }
          
          .generate-workflow-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 200%;
            height: 100%;
            background: linear-gradient(
              90deg, 
              rgba(66, 133, 244, 0.7), 
              rgba(123, 97, 255, 0.7), 
              rgba(66, 133, 244, 0.7)
            );
            z-index: 0;
            animation: gradientMove 3s infinite linear;
          }
          
          @keyframes gradientMove {
            0% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(50%);
            }
          }
          
          .generate-workflow-button:active {
            color: white !important;
          }
          
          .generate-workflow-button:focus {
            color: white !important;
          }
          
          .generate-workflow-button:disabled {
            background: #f0f0f0 !important;
            color:rgba(0, 0, 0, 0.2)  !important;
          }
          
          .generate-workflow-button:disabled > span {
            color: rgba(0, 0, 0, 0.2) !important;
          }
          
          .generate-workflow-button:disabled .anticon {
            color: #999 !important;
          }
          
          /* Suggestion cards */
          .suggestion-card:hover {
            background-color: #f0f7ff !important;
            transform: translateX(4px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            border-color: #4285f4;
          }
          
          /* Template cards */
          .template-card {
            transition: all 0.2s ease;
            background-color: #ffffff !important;
          }
          
          .template-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 6px 16px rgba(0,0,0,0.1);
          }
          
          /* Category buttons */
          .category-button {
            border-radius: 16px !important;
            margin-right: 8px !important;
            margin-bottom: 8px !important;
            background-color: #f5f5f5 !important;
            border-color: #d9d9d9 !important;
            color: #666 !important;
          }
          
          .category-button:hover {
            background-color: #e8e8e8 !important;
            color: #333 !important;
          }
          
          .selected-category {
            background: linear-gradient(90deg, #4285f4, #7b61ff) !important;
            color: white !important;
            border: none !important;
          }
          
          .selected-category:hover {
            background: linear-gradient(90deg, #3b78e7, #6a4ff2) !important;
            color: white !important;
          }
          
          /* Responsive styles */
          @media (max-width: 768px) {
            .workflow-tabs .ant-tabs-tab {
              padding: 8px 16px;
            }
          }
        `}</style>
      </Modal>

      {isAIModalVisible && (
        <AIWorkflowGeneratorModal 
          visible={isAIModalVisible}
          onCancel={() => {
            setIsAIModalVisible(false);
            if (modalStack.includes('template')) {
              setModalStack(modalStack.filter(m => m !== 'template'));
              setIsTemplateVisible(true);
            }
          }}
          onGenerate={onGenerate}
        />
      )}

      {isTutorialMode && (
        <Tour
          open={true}
          onClose={() => {
            setIsTutorialMode(false);
            setTutorialStep(0);
          }}
          current={tutorialStep}
          onChange={setTutorialStep}
          steps={tutorialSteps}
          zIndex={1000}
        />
      )}
    </>
  );
};

export default TemplateModal;